import React, { useEffect, useRef, useState } from "react";

import { cls } from "app/utils";
import UserAvatar from "app/components/UserAvatar";

import styles from "./styles.module.scss";

export interface ViewingOption {
  value: string;
  label: string;
  avatar: string | null;
}

interface Props {
  options: ViewingOption[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * "My Sessions" header chip (CN-1323). A family head switches the Bookings page
 * between their own sessions, a single dependent's, or the whole family. The
 * chip shows the current selection; the menu lists every option.
 */
const ViewingChip = ({ options, value, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = options.find((o) => o.value === value) ?? options[0];

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={styles.chip}
        onClick={() => setOpen((o) => !o)}
      >
        <UserAvatar
          sizeWidth={20}
          sizeHeight={20}
          altName={selected.label}
          srcName={selected.avatar ?? "default"}
          userName={selected.label}
          className={cls("avatar", styles.chip_avatar)}
        />
        <span className={styles.chip_label}>{selected.label}</span>
        <i
          className={cls("ico ico-chevron", styles.caret, {
            [styles.caret_open]: open,
          })}
        />
      </button>

      {open && (
        <div className={styles.menu}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cls(styles.menu_item, {
                [styles.menu_item_active]: option.value === value,
              })}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <UserAvatar
                sizeWidth={24}
                sizeHeight={24}
                altName={option.label}
                srcName={option.avatar ?? "default"}
                userName={option.label}
                className={cls("avatar", styles.menu_avatar)}
              />
              <span className={styles.menu_label}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ViewingChip;
